import React, { useState, useEffect, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter as Router, Routes, Route, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Sparkles } from "lucide-react";
import Home from "./components/Home";
import NotFound from "./components/NotFound";
import { trackPageview } from "./analytics";
import OidcContinue from "./components/OidcContinue";
import Articles from "./components/Articles";
import Lectures from "./components/Lectures";
import SeriesPage from "./components/SeriesPage";
import Cases from "./components/Cases";
import Forum from "./components/Forum";
import Tools from "./components/Tools";
import Events from "./components/Events";
import Profile from "./components/Profile";
import ContentDetail from "./components/ContentDetail";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Terms from "./components/Terms";
import Help from "./components/Help";
import SearchResults from "./components/SearchResults";
import SavedItems from "./components/SavedItems";
import AiSearch from "./components/AiSearch";
import SecondOpinion from "./components/SecondOpinion";
import DoctorDirectory from "./components/DoctorDirectory";
import DoctorProfile from "./components/DoctorProfile";
import About from "./components/About";
import Manufacturers from "./components/Manufacturers";
import ManufacturerDetail from "./components/ManufacturerDetail";
import EditorialBoard from "./components/EditorialBoard";
import VerifyEmail from "./components/VerifyEmail";
import ResetPassword from "./components/ResetPassword";
import AdminPanel from "./components/admin/AdminPanel";
import Login from "./components/Login";
import Register from "./components/Register";
import FeedbackModal from "./components/FeedbackModal";
import { Helmet } from "react-helmet-async";
import { AuthProvider, useAuth } from "./AuthContext";
import { SpecialtyProvider, SPECIALTIES, useSpecialty } from "./SpecialtyContext";
import "./App.css";
import { MainNav, AccountNav } from "./components/SiteNavigation";
import Community, { CommunitySection } from "./components/Community";
import "./components/SiteNavigation.css";

function SpecialtySidebar() {
  const { specialty, setSpecialty } = useSpecialty();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Верхнее меню главное: смена специальности остаётся в текущем разделе
  // (если раздел фильтруется по специальности), иначе — на главную.
  const pick = (s) => {
    setSpecialty(s);
    const seg = pathname.split("/")[1] || "";
    navigate(["articles", "lectures", "cases", "events", "discussions", "editors"].includes(seg) ? `/${seg}` : "/");
  };
  return (
    <nav aria-label="Специальности">
      <div className="sidebar-title">Специальность</div>
      <ul>
        <li>
          <button className={`nav-link${!specialty ? " active" : ""}`} onClick={() => pick(null)}>Весь портал</button>
        </li>
        {SPECIALTIES.map(s => (
          <li key={s.id}>
            <button className={`nav-link${specialty?.id === s.id ? " active" : ""}`} onClick={() => pick(s)}>{s.label}</button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function HeaderSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const submit = e => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };
  // Умный поиск: ответ по материалам портала; вопрос из поля подставляется и запускается
  const ask = () => navigate(q.trim() ? `/ai-search?q=${encodeURIComponent(q.trim())}` : "/ai-search");
  return (
    <form onSubmit={submit} className="header-search-form">
      <Search size={15} strokeWidth={2.2} className="header-search-icon" />
      <input
        type="text" value={q} onChange={e => setQ(e.target.value)}
        placeholder="Поиск по порталу…"
        className="header-search-input"
      />
      <button type="button" className="header-smart-btn" onClick={ask} title="Умный поиск: задайте вопрос своими словами — ответ по материалам портала">
        <Sparkles size={14} strokeWidth={2.2} /> Спросить
      </button>
    </form>
  );
}

// Считает открытия страниц для собственной анонимной статистики (см. analytics.js)
const PAGE_TITLES = {
  "/articles": "Статьи", "/lectures": "Материалы", "/cases": "Клинические случаи", "/discussions": "Обсуждения",
  "/tools": "Инструменты", "/events": "События", "/about": "О нас", "/help": "Помощь", "/manufacturers": "Партнёры",
  "/editors": "Редакция портала", "/privacy": "Политика конфиденциальности", "/terms": "Пользовательское соглашение",
  "/community": "Сообщество", "/reset-password": "Новый пароль", "/saved": "Сохранённое", "/doctors": "Справочник коллег", "/second-opinion": "Второе мнение", "/search": "Поиск", "/profile": "Профиль",
};

/** Название вкладки по разделу; страницы материалов задают своё название сами. */
function PageTitle() {
  const { pathname } = useLocation();
  const name = PAGE_TITLES[pathname.replace(/\/+$/, "")];
  return name ? <Helmet><title>{`${name} — Современный Врач`}</title></Helmet> : null;
}

function PageTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => { trackPageview(pathname, search); }, [pathname, search]);
  return null;
}

function AppLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const authDestination = useRef(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const requestLogin = () => {
    authDestination.current = location;
    setShowLogin(true);
  };
  const requestRegister = () => {
    authDestination.current = location;
    setShowRegister(true);
  };
  const closeAuth = () => {
    setShowLogin(false);
    setShowRegister(false);
    authDestination.current = null;
  };
  const completeAuthentication = () => {
    const destination = authDestination.current;
    if (destination) {
      navigate(`${destination.pathname}${destination.search}${destination.hash}`, {
        replace: true, state: destination.state,
      });
    }
  };
  return (
    <div className="site-wrapper">
      {/* ШАПКА */}
      <header className="site-header">
        <div className="header-inner">
          <Link to="/" className="header-brand-link" aria-label="Современный Врач — на главную">
            <img src="/logo-full.png" alt="Современный Врач" className="header-logo-img" />
            <div className="header-titles">
              <div className="header-sup">Научно-образовательный портал</div>
              <div className="header-tags">статьи&nbsp;•&nbsp;материалы&nbsp;•&nbsp;клинические случаи&nbsp;•&nbsp;инструменты&nbsp;•&nbsp;обсуждения</div>
            </div>
          </Link>
          <HeaderSearch />
          <div className="header-auth">
            <NavLink to="/about" className="header-about-link">О нас</NavLink>
            <AccountNav onLoginRequest={requestLogin} />
          </div>
        </div>
        {/* ОСНОВНАЯ НАВИГАЦИЯ */}
        <MainNav />
      </header>

      {/* ОСНОВНАЯ ЗОНА */}
      <div className="layout">
        {/* БОКОВОЕ МЕНЮ: специальности */}
        <aside className="sidebar">
          <SpecialtySidebar />
        </aside>

        {/* КОНТЕНТ */}
        <main className="content">
          <PageTracker />
          <PageTitle />
          <Routes>
            <Route path="/" element={<Home onLoginRequest={requestLogin} />} />
            <Route path="/articles" element={<Articles onLoginRequest={requestLogin} />} />
            <Route path="/articles/:id" element={<ContentDetail kind="articles" onLoginRequest={requestLogin} />} />
            <Route path="/lectures" element={<Lectures />} />
            <Route path="/series/:id" element={<SeriesPage />} />
            <Route path="/lectures/:id" element={<ContentDetail kind="lectures" onLoginRequest={requestLogin} />} />
            <Route path="/cases" element={<Cases />} />
            <Route path="/cases/:id" element={<ContentDetail kind="cases" onLoginRequest={requestLogin} />} />
            <Route path="/community" element={<Community />} />
            <Route path="/discussions" element={
              <CommunitySection section="discussions" onLoginRequest={requestLogin} onRegisterRequest={requestRegister}>
                <Forum onLoginRequest={requestLogin} />
              </CommunitySection>
            } />
            <Route path="/tools" element={<Tools />} />
            <Route path="/events" element={<Events onLoginRequest={requestLogin} />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/saved" element={<SavedItems />} />
            <Route path="/ai-search" element={<AiSearch onLoginRequest={requestLogin} />} />
            <Route path="/second-opinion" element={
              <CommunitySection section="second-opinion" onLoginRequest={requestLogin} onRegisterRequest={requestRegister}>
                <SecondOpinion onLoginRequest={requestLogin} />
              </CommunitySection>
            } />
            <Route path="/doctors" element={
              <CommunitySection section="doctors" onLoginRequest={requestLogin} onRegisterRequest={requestRegister}>
                <DoctorDirectory onLoginRequest={requestLogin} />
              </CommunitySection>
            } />
            <Route path="/editors" element={<EditorialBoard />} />
            <Route path="/doctors/:id" element={<DoctorProfile onLoginRequest={requestLogin} />} />
            <Route path="/manufacturers" element={<Manufacturers />} />
            <Route path="/manufacturers/:slug" element={<ManufacturerDetail onLoginRequest={requestLogin} />} />
            <Route path="/about" element={<About onLoginRequest={requestLogin} />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword onLoginRequest={requestLogin} />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/help" element={<Help />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/oidc/continue" element={<OidcContinue onLoginRequest={requestLogin} />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <div className="site-footer-brand">
            <Link to="/" aria-label="Современный Врач — на главную"><img src="/logo-full.png" alt="Современный Врач" className="site-footer-logo" /></Link>
            <p>Для специалистов здравоохранения</p>
            <a href="mailto:info@svrach.ru">info@svrach.ru</a>
          </div>
          <nav className="site-footer-col" aria-label="Разделы портала">
            <h4>Разделы</h4>
            <ul>
              <li><NavLink to="/articles">Статьи</NavLink></li>
              <li><NavLink to="/lectures">Материалы</NavLink></li>
              <li><NavLink to="/cases">Клинические случаи</NavLink></li>
              <li><NavLink to="/tools">Инструменты</NavLink></li>
              <li><NavLink to="/events">События</NavLink></li>
              <li><NavLink to="/community">Сообщество</NavLink></li>
            </ul>
          </nav>
          <nav className="site-footer-col" aria-label="О портале">
            <h4>О портале</h4>
            <ul>
              <li><NavLink to="/about">О нас</NavLink></li>
              <li><NavLink to="/editors">Редакция портала</NavLink></li>
              <li><NavLink to="/manufacturers">Партнёры</NavLink></li>
              <li><NavLink to="/help">Помощь</NavLink></li>
              <li><button type="button" className="site-footer-link" onClick={() => setShowFeedback(true)}>Сообщить об ошибке</button></li>
            </ul>
          </nav>
          <nav className="site-footer-col" aria-label="Документы">
            <h4>Документы</h4>
            <ul>
              <li><NavLink to="/privacy">Политика конфиденциальности</NavLink></li>
              <li><NavLink to="/terms">Пользовательское соглашение</NavLink></li>
            </ul>
          </nav>
        </div>
        <div className="site-footer-bottom">
          <div className="site-footer-bottom-inner">
            <span>© 2026 Научно-образовательный портал «Современный Врач»</span>
            <span><a href="https://www.a11group.tech/" target="_blank" rel="noopener noreferrer">Разработано A11 Group</a></span>
            <span>Дизайн: <a href="https://vk.ru/designmirosha" target="_blank" rel="noopener noreferrer">Елена Миронова</a></span>
          </div>
        </div>
      </footer>

      {/* MODALS */}
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} user={user} token={localStorage.getItem("sv_token")} />}
      {showLogin && (
        <Login
          onClose={closeAuth}
          onSuccess={completeAuthentication}
          onSwitchToRegister={() => { setShowLogin(false); setShowRegister(true); }}
          onFeedback={() => { setShowLogin(false); setShowFeedback(true); }}
        />
      )}
      {showRegister && (
        <Register
          onClose={closeAuth}
          onSuccess={completeAuthentication}
          onSwitchToLogin={() => { setShowRegister(false); setShowLogin(true); }}
          onFeedback={() => { setShowRegister(false); setShowFeedback(true); }}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <SpecialtyProvider>
        <AuthProvider>
          <Router>
            <AppLayout />
          </Router>
        </AuthProvider>
      </SpecialtyProvider>
    </HelmetProvider>
  );
}

export default App;
